import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { BACKEND_URL } from '../lib/apiBase';
import { rewriteUploadsInHtml } from '../lib/uploadsUrl';

/**
 * Pull the very first heading text out of the rich HTML so the section
 * title on the homepage stays in sync with whatever the admin renamed the
 * guide to. Falls back to the translated default if no heading is present.
 */
function extractFirstHeadingText(html) {
  if (!html || typeof html !== 'string') return '';
  const m = html.match(/<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/i);
  if (!m) return '';
  // Strip any inline tags (span/strong/etc.) then decode HTML entities
  // (e.g. "&amp;" → "&", "&nbsp;" → " ") via the browser's parser.
  const stripped = m[1].replace(/<[^>]+>/g, '').trim();
  if (typeof document === 'undefined') return stripped;
  const tmp = document.createElement('textarea');
  tmp.innerHTML = stripped;
  return tmp.value;
}

/**
 * Homepage teaser for the Sitges Guide page. Loads the guide HTML from the
 * backend, clips it to a fixed visible height with a soft fade-out gradient,
 * and shows a "Continue reading" CTA that takes the visitor to the full page.
 */
export function SitgesGuidePreviewSection() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [html, setHtml] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/site-content/sitges_guide?lang=${lang}`);
        const data = await res.json();
        setHtml(rewriteUploadsInHtml(data.html || ''));
      } catch (err) {
        console.error('Failed to load Sitges Guide preview', err);
      } finally {
        setLoaded(true);
      }
    })();
  }, [lang]);

  const sectionTitle = useMemo(() => {
    return extractFirstHeadingText(html) || t('sitges_guide_title');
  }, [html, t]);

  const goToGuide = () => {
    navigate('/Sitges-Guide');
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  if (!loaded || !html.trim()) return null;

  return (
    <section
      className="py-[30px] sm:py-[40px]"
      data-testid="sitges-guide-preview-section"
      id="sitges-guide-preview"
    >
      <div className="max-w-[1280px] mx-auto px-5 lg:px-6">
        <h2
          className="h-font text-[#0F3B63] text-[29px] sm:text-[38px] font-bold tracking-tight"
          data-testid="sitges-guide-preview-title"
        >
          {sectionTitle}
        </h2>

        {/* Clipped preview with fade-out gradient */}
        <div className="relative mt-7">
          <div
            className="sitges-guide-content sitges-guide-preview prose prose-lg max-w-none
                       prose-headings:text-[#1a4d7a] prose-headings:font-bold
                       prose-p:text-[#2d5f8d] prose-li:text-[#2d5f8d]
                       prose-a:text-[#0184ff] prose-strong:text-[#1a4d7a]
                       prose-img:rounded-xl
                       max-h-[760px] sm:max-h-[820px] overflow-hidden"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="sitges-guide-preview-content"
            dangerouslySetInnerHTML={{ __html: html }}
          />
          {/* Fade overlay — never blocks pointer so links/images stay clickable above it */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-40
                       bg-gradient-to-b from-transparent via-white/85 to-white"
          />
        </div>

        {/* Continue reading CTA — matches the "Book now" pill style */}
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={goToGuide}
            className="group inline-flex items-center gap-2 rounded-full
                       bg-[#3c84ff] hover:bg-[#2a6fe0] text-white font-semibold
                       text-[14px] sm:text-[15px] px-7 h-11
                       active:scale-[0.97] transition-all duration-200"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="sitges-guide-continue-reading-btn"
          >
            <span>{t('continue_reading')}</span>
            <ArrowRight
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
              strokeWidth={2.5}
            />
          </button>
        </div>
      </div>
    </section>
  );
}

export default SitgesGuidePreviewSection;
